import { ConnectProto } from "@racoon/api";
import { Namespace, type TFn } from "@racoon/i18n";
import { getEnumName } from "@racoon/lib";
import { compareIds } from "~/shared/lib/compare-ids";

const { DriverIncidentType, JobStatus } = ConnectProto;

/** What the jobs table's Инцидент cell says about a job. */
export type JobIncidentNote =
  | {
      kind: "incident";
      type: ConnectProto.DriverIncidentType;
      /** The author's own words: the incident's explanation, or the shift's force-completion's. */
      explanation?: string;
    }
  /**
   * «Не успели (долг)»: the shift ended before reaching the job, or the job expired unrouted
   * at midnight. The dispatcher re-issues the order on demand.
   */
  | {
      kind: "debt";
      /** A dispatcher's words, when they recorded it. */
      explanation?: string;
    };

/** The job's newest incident: the one its reason reads from. */
export const newestIncident = (
  job: ConnectProto.Job,
  incidents: readonly ConnectProto.DriverIncident[],
) =>
  incidents
    .filter((incident) => incident.jobId === job.id)
    .sort((a, b) => compareIds(b.id, a.id))[0];

/** The words a note reads as: its incident type, or «Не успели (долг)». */
export const incidentNoteLabel = (t: TFn, note: JobIncidentNote) => {
  if (note.kind === "debt") return t(Namespace.Orders, "jobMap.overdue");
  const type = getEnumName(ConnectProto.DriverIncidentTypeSchema, note.type);
  return type ? t(Namespace.Enum, type) : undefined;
};

/**
 * Why a job was not carried out. An incident recorded on it — by its driver or a
 * dispatcher — says so; the newest one counts. The system's EXPIRED one, recorded when
 * the job's day ended without a route, is debt. A невывоз with no incident at all is
 * the shift's — the incident a dispatcher force-completed it with — and without that it
 * is debt too.
 * EXPIRED counts only while the job is a невывоз; `status` reads the job as another outcome.
 */
export const jobIncidentNote = (
  job: ConnectProto.Job,
  incidents: readonly ConnectProto.DriverIncident[],
  status: ConnectProto.JobStatus = job.status,
): JobIncidentNote | undefined => {
  const newest = newestIncident(job, incidents);
  if (newest?.type === DriverIncidentType.EXPIRED) {
    return status === JobStatus.NOT_COMPLETED
      ? { kind: "debt", explanation: newest.explanation || undefined }
      : undefined;
  }
  if (newest) {
    return { kind: "incident", type: newest.type, explanation: newest.explanation || undefined };
  }
  if (status !== JobStatus.NOT_COMPLETED) return undefined;
  const forceCompletion = job.driverOnShift?.forceCompleteIncident;
  if (forceCompletion?.type) {
    return {
      kind: "incident",
      type: forceCompletion.type,
      explanation: forceCompletion.explanation || undefined,
    };
  }
  return { kind: "debt" };
};

/**
 * Where the Инцидент column puts a job, as the server's `incident` sort does: the
 * incident type's enum number, debt as EXPIRED (the type the midnight expiry records);
 * undefined when there is nothing to show.
 */
export const jobIncidentRank = (
  job: ConnectProto.Job,
  incidents: readonly ConnectProto.DriverIncident[],
): ConnectProto.DriverIncidentType | undefined => {
  const note = jobIncidentNote(job, incidents);
  if (!note) return undefined;
  return note.kind === "debt" ? DriverIncidentType.EXPIRED : note.type;
};
